import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Printer, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  AVERY_CATALOG, SUPPORTED_CODES, normalizeProductCode, labelsPerSheet, slotRect,
  paginate, sheetCount, validateStart, formatLabel, labelIssues,
} from "@/lib/averyLabels";

const TITLES = { toys: "Toy labels", clothing: "Clothing labels", giftcards: "Gift card labels" };

const PRINT_CSS = `
.avery-print-blocked-msg{display:none}
@media print{
  @page{size:Letter;margin:0}
  html,body{margin:0!important;padding:0!important;background:#fff!important;height:auto!important;overflow:visible!important}
  body.avery-printing > *:not(.avery-print-root){display:none!important}
  .avery-print-root{position:static!important;display:block!important;background:none!important;overflow:visible!important;padding:0!important}
  .avery-print-root .avery-screen-only{display:none!important}
  .avery-print-root .avery-scroll{overflow:visible!important;max-height:none!important;padding:0!important;background:none!important}
  .avery-print-root .avery-sheets{display:block!important;gap:0!important;margin:0!important}
  .avery-print-root .avery-sheet{margin:0!important;box-shadow:none!important;break-after:page;page-break-after:always}
  .avery-print-root .avery-sheet:last-child{break-after:auto;page-break-after:auto}
  .avery-print-root .avery-cell{border:0!important}
  .avery-print-root.avery-blocked .avery-sheets{display:none!important}
  .avery-print-root.avery-blocked .avery-print-blocked-msg{display:block!important;padding:1in;font:14pt sans-serif}
}`;

function Sheet({ product, cells, listType, opts, index, total }) {
  return (
    <div className="avery-sheet" data-testid={`avery-sheet-${index}`}
         style={{ position: "relative", width: "8.5in", height: "11in", background: "#fff", overflow: "hidden", flex: "none", boxSizing: "border-box" }}>
      {cells.map(({ slot, label }) => {
        const r = slotRect(product, slot);
        const f = formatLabel(label, listType, opts);
        return (
          <div key={label.key} className="avery-cell" data-label-key={label.key}
               style={{ position: "absolute", left: `${r.left}in`, top: `${r.top}in`, width: `${r.width}in`, height: `${r.height}in`,
                        boxSizing: "border-box", padding: "0.06in 0.1in", overflow: "hidden", border: "1px dashed #bbb",
                        fontFamily: "Arial, Helvetica, sans-serif", fontSize: "9pt", lineHeight: 1.15, color: "#000", overflowWrap: "anywhere" }}>
            <div style={{ fontWeight: 700 }}>{f.head || "\u00A0"}</div>
            {f.body.map((t, i) => <div key={i}>{t}</div>)}
            <div style={{ fontWeight: 700, marginTop: "0.03in" }} data-ids>{f.ids}</div>
          </div>
        );
      })}
      <span className="avery-screen-only" style={{ position: "absolute", right: 4, bottom: 2, fontSize: 10, color: "#999" }}>Sheet {index + 1} of {total}</span>
    </div>
  );
}

export default function AveryLabelPrintModal({ year, listType, onClose }) {
  const [productInput, setProductInput] = useState("");
  const [genderLabels, setGender] = useState("boy_girl");
  const [sizeLabels, setSize] = useState("abbreviated");
  const [idLabels, setIds] = useState("short");
  const [sizes, setSizes] = useState({ pant: true, shirt: true, pj: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [start, setStart] = useState("1");
  const [overflow, setOverflow] = useState([]);
  const [retry, setRetry] = useState(0);
  const reqId = useRef(0);
  const knownKeys = useRef(false);
  const previewRef = useRef(null);

  const { product, error: productError } = normalizeProductCode(productInput);

  // Fetch (stale requests cancelled). Selection survives refetches; keys are stable.
  useEffect(() => {
    const id = ++reqId.current;
    const ctrl = new AbortController();
    setLoading(true); setError("");
    api.get("/sharing-tree/labels", {
      params: { year, list_type: listType, gender_labels: genderLabels, size_labels: sizeLabels, id_labels: idLabels },
      signal: ctrl.signal,
    }).then(({ data: d }) => {
      if (id !== reqId.current) return;
      setData(d);
      const keys = new Set((d?.labels || []).map((l) => l.key));
      const initialize = !knownKeys.current;
      knownKeys.current = true;
      setSelected((prev) => {
         if (initialize) return keys;
        return new Set([...prev].filter((k) => keys.has(k)));
      });
      setLoading(false);
    }).catch((e) => {
      if (id !== reqId.current || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      setError(formatApiError(e)); setLoading(false);
    });
    return () => ctrl.abort();
  }, [year, listType, genderLabels, sizeLabels, idLabels, retry]);

  // Print scoping while open
  useEffect(() => {
    document.body.classList.add("avery-printing");
    return () => document.body.classList.remove("avery-printing");
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const labels = useMemo(() => data?.labels || [], [data]);
  const opts = useMemo(() => ({ sizes }), [sizes]);
  const yearMismatch = data && Number(data.year) !== Number(year);
  const issues = useMemo(() => labelIssues(labels, selected, listType, opts), [labels, selected, listType, opts]);
  const startError = product ? validateStart(start, product) : "";
  const printable = issues.chosen;
  const sheets = useMemo(() => (product && !startError ? paginate(printable, product, Number(start)) : []), [printable, product, start, startError]);
  const totalSheets = product && !startError ? sheetCount(printable.length, product, Number(start)) : 0;

  const measure = useCallback(() => {
    const root = previewRef.current;
    if (!root) return;
    const bad = [];
    root.querySelectorAll(".avery-cell").forEach((c) => {
      if (c.scrollHeight > c.clientHeight + 1 || c.scrollWidth > c.clientWidth + 1) bad.push(c.getAttribute("data-label-key"));
    });
    setOverflow((prev) => (prev.join("|") === bad.join("|") ? prev : bad));
  }, []);
  useLayoutEffect(() => {
    measure();
    const root = previewRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [measure, sheets, opts]);

  const blockers = [];
  if (loading) blockers.push("Labels are still loading.");
  if (error) blockers.push("Labels could not be loaded.");
  if (yearMismatch) blockers.push(`Source year mismatch: data is for ${data.year}, not ${year}.`);
  if (productError) blockers.push(productError);
  if (startError) blockers.push(startError);
  if (!printable.length && data && !yearMismatch) blockers.push("Select at least one label.");
  if (issues.empty.length) blockers.push(`${issues.empty.length} selected label(s) would print with no ${listType === "toys" ? "gift text" : "size lines"}. Deselect them or enable more fields.`);
  if (overflow.length && !productError && !startError) blockers.push(`${overflow.length} label(s) overflow their cell. Choose a larger stock, fewer fields, or fewer tags.`);
  const canPrint = blockers.length === 0;

  function toggle(key) {
    setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }
  const border = { borderColor: "var(--fb-border)" };
  const chk = (label, value, set, testId) => (
    <label className="inline-flex items-center gap-1.5 text-sm">
      <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} data-testid={testId} /> {label}
    </label>
  );

  return createPortal(
    <div className={`avery-print-root${canPrint ? "" : " avery-blocked"} fixed inset-0 z-[100] bg-white overflow-y-auto`} data-testid="avery-modal" role="dialog" aria-modal="true" aria-label={TITLES[listType]}>
      <style>{PRINT_CSS}</style>
      <div className="avery-print-blocked-msg" data-testid="avery-print-blocked-msg">
        Labels cannot be printed yet. Return to the label dialog and resolve the listed issues.
      </div>
      <div className="avery-screen-only p-4 sm:p-6 space-y-4 border-b" style={border}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>{TITLES[listType]} — {year}</h3>
          <button type="button" onClick={onClose} aria-label="Close label dialog" data-testid="avery-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="avery-product" className="block text-sm font-semibold mb-1">Avery product</label>
            <input id="avery-product" list="avery-products" value={productInput} onChange={(e) => setProductInput(e.target.value)}
                   placeholder="Choose or type, e.g. Avery 5160" data-testid="avery-product"
                   className="w-full px-3 py-2 rounded-lg border" style={border} />
            <datalist id="avery-products">{SUPPORTED_CODES.map((c) => <option key={c} value={c} />)}</datalist>
            <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
              Supported: {Object.values(AVERY_CATALOG).map((p) => (
                <span key={p.code}><a href={p.sourceUrl} target="_blank" rel="noreferrer" className="underline">{[p.code, ...p.aliases].join("/")}</a>{" "}</span>
              ))}
              (Letter, no scaling; print at 100%).
            </p>
            {productError && productInput && <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }}>{productError}</p>}
          </div>
          <div>
            <label htmlFor="avery-start" className="block text-sm font-semibold mb-1">Start position{product ? ` (1–${labelsPerSheet(product)})` : ""}</label>
            <input id="avery-start" type="number" value={start} onChange={(e) => setStart(e.target.value)} data-testid="avery-start"
                   className="w-28 px-3 py-2 rounded-lg border tabular-nums" style={border} aria-invalid={!!startError} />
            <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>First sheet only, left to right then down.</p>
            {startError && <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }}>{startError}</p>}
          </div>
          <div className="text-sm" data-testid="avery-totals">
            <div className="font-semibold">Totals</div>
            <div>{printable.length} label(s) on {totalSheets} sheet(s)</div>
            {product && <div data-testid="avery-stock-details" className="mt-1 text-xs">
              Layout {product.code}: {product.width} × {product.height} inch labels,
              {" "}{labelsPerSheet(product)} per sheet ({product.cols} columns × {product.rows} rows).
              {" "}US Letter: 8.5 × 11 inches.
            </div>}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {chk("Boy/Girl gender labels", genderLabels === "boy_girl", (v) => setGender(v ? "boy_girl" : "standard"), "avery-opt-gender")}
          {chk("Abbreviated sizes", sizeLabels === "abbreviated", (v) => setSize(v ? "abbreviated" : "standard"), "avery-opt-size")}
          {chk("Short IDs", idLabels === "short", (v) => setIds(v ? "short" : "standard"), "avery-opt-ids")}
          {listType === "clothing" && (<>
            {chk("Pant", sizes.pant, (v) => setSizes((s) => ({ ...s, pant: v })), "avery-line-pant")}
            {chk("Shirt", sizes.shirt, (v) => setSizes((s) => ({ ...s, shirt: v })), "avery-line-shirt")}
            {chk("PJ", sizes.pj, (v) => setSizes((s) => ({ ...s, pj: v })), "avery-line-pj")}
          </>)}
          {loading && data && <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Updating…</span>}
        </div>

        {error && (
          <div role="alert" className="p-3 rounded-xl fb-status-error text-sm flex items-center justify-between gap-3" data-testid="avery-error">
            <span>{error}</span>
            <button type="button" className="underline font-semibold" onClick={() => setRetry((n) => n + 1)} data-testid="avery-retry">Retry</button>
          </div>
        )}
        {loading && !data && !error && <div className="h-16 rounded-xl animate-pulse" style={{ background: "var(--fb-border)" }} data-testid="avery-loading" />}
        {data && labels.length === 0 && !error && (
          <div className="py-6 text-center text-sm" style={{ color: "var(--fb-text-muted)" }} data-testid="avery-empty">No labels available for {year}.</div>
        )}

        {labels.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-2 text-sm">
              <span className="font-semibold">Labels ({selected.size}/{labels.length} selected)</span>
              <button type="button" className="underline" data-testid="avery-select-all" onClick={() => setSelected(new Set(labels.map((l) => l.key)))}>Select all</button>
              <button type="button" className="underline" data-testid="avery-select-none" onClick={() => setSelected(new Set())}>Select none</button>
            </div>
            <ul className="max-h-48 overflow-y-auto border rounded-lg divide-y text-sm" style={border}>
              {labels.map((l) => (
                <li key={l.key} className="px-3 py-1.5 flex items-center gap-3" style={{ borderColor: "var(--fb-border)" }}>
                  <input type="checkbox" id={`avery-sel-${l.key}`} checked={selected.has(l.key)} onChange={() => toggle(l.key)} data-testid={`avery-sel-${l.key}`} />
                  <label htmlFor={`avery-sel-${l.key}`} className="flex-1 font-mono text-xs">{l.household_id} / {l.member_id} — {[l.gender, l.age].filter((x) => x !== null && x !== "").join(" ")}</label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {blockers.length > 0 && data && (
          <div role="alert" className="p-3 rounded-xl text-sm space-y-1" style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }} data-testid="avery-blockers">
            {blockers.map((b) => <div key={b} className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {b}</div>)}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <p className="text-xs mr-auto self-center" style={{ color: "var(--fb-text-muted)" }}>
            Print on US Letter at actual size / 100%. Turn off browser headers and footers.
            Test on plain paper against your label sheet before printing on labels.
          </p>
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-lg border text-sm" style={border}>Close</button>
          <button type="button" disabled={!canPrint} onClick={() => canPrint && window.print()} data-testid="avery-print"
                  className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Printer className="w-4 h-4" /> Print {printable.length} label(s)
          </button>
        </div>
      </div>

      <div className="avery-scroll overflow-x-auto p-4 sm:p-6" style={{ background: "#E7E5E0" }}>
        <div ref={previewRef} className="avery-sheets flex flex-col gap-4 w-max mx-auto">
          {product && !startError && sheets.map((cells, i) => (
            <Sheet key={i} product={product} cells={cells} listType={listType} opts={opts} index={i} total={sheets.length} />
          ))}
          {!product && <div className="avery-screen-only text-sm p-6" style={{ color: "var(--fb-text-muted)" }}>Choose an Avery product to see an exact-size preview.</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
